// Utility functions for formatting - Albanian/European standards

/**
 * Format currency in Albanian Lek: 1,234.00 L
 * @param {number} amount - The amount to format
 * @param {boolean} showCurrency - Whether to show "L" suffix
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showCurrency = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showCurrency ? '0.00 L' : '0.00';
  }
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  return showCurrency ? `${formatted} L` : formatted;
};

/**
 * Format currency in Euro: €1,234.00
 * @param {number} amount - The amount to format
 * @param {boolean} showSymbol - Whether to show "€" symbol
 * @returns {string} Formatted currency string
 */
export const formatEuro = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '€0.00' : '0.00';
  }
  
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  return showSymbol ? `€${formatted}` : formatted;
};

/**
 * Convert Lek to Euro
 * @param {number} lekAmount - Amount in Lek
 * @param {number} exchangeRate - Exchange rate (Lek per Euro, default: 100)
 * @returns {number} Amount in Euro
 */
export const lekToEuro = (lekAmount, exchangeRate = 100) => {
  if (!lekAmount || isNaN(lekAmount) || !exchangeRate) return 0;
  return lekAmount / exchangeRate;
};

/**
 * Format currency short version for cards: 2.1K L (only for large numbers > 10000)
 * Otherwise show full: 1,234.00 L
 */
export const formatCurrencyCompact = (amount, showCurrency = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showCurrency ? '0.00 L' : '0.00';
  }
  
  // For amounts less than 10000, show full format
  if (Math.abs(amount) < 10000) {
    return formatCurrency(amount, showCurrency);
  }
  
  // For larger amounts, show compact with 1 decimal
  const compact = (amount / 1000).toFixed(1);
  return showCurrency ? `${compact}K L` : `${compact}K`;
};

/**
 * Format date in European style: DD.MM.YYYY
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '-';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('sq-AL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format time: HH:MM
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (dateInput) => {
  if (!dateInput) return '-';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleTimeString('sq-AL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format date and time: DD.MM.YYYY HH:MM
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '-';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleString('sq-AL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format relative time: "5 min më parë", "2 orë më parë"
 * @param {string|Date} dateInput - Date to compare
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '-';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '-';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'tani';
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours} orë`;
  if (diffDays < 7) return `${diffDays} ditë`;
  
  return formatDate(dateInput);
};

/**
 * Format phone number: +355 69 XXX XXXX
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone string
 */
export const formatPhone = (phone) => {
  if (!phone) return '-';
  
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  // Format Albanian numbers
  if (digits.length === 12 && digits.startsWith('355')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  
  if (digits.length === 10 && digits.startsWith('06')) {
    return `+355 ${digits.slice(1, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  
  return phone;
};

/**
 * Format number with thousand separator
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return new Intl.NumberFormat('de-DE').format(num);
};

export default {
  formatCurrency,
  formatEuro,
  lekToEuro,
  formatCurrencyCompact,
  formatDate,
  formatTime,
  formatDateTime,
  formatRelativeTime,
  formatPhone,
  formatNumber
};
